import { getPartyOccupancy, PartyOccupancyInput, PartyOccupancyOptions } from './get-party-occupancy';

export interface SeatedConflictCandidate extends PartyOccupancyInput {
  _id?: string;
  resources?: string[];
  size?: number;
}

export interface SeatedConflictCheckOptions extends PartyOccupancyOptions {
  allowMultipleParties?: boolean;
  partySizeForTimeSlots?: boolean;
  capacityMax: number;
}

interface OccupancyWithSize {
  start: number;
  end: number;
  size: number;
}

/**
 * Client mirror of the backend findSeatedConflicts guard: the active parties already seated on
 * `resourceId` whose occupancy overlaps `party`'s window, using the shared getPartyOccupancy
 * primitive. Returns no parties when the table is free, or when a communal table
 * (allowMultipleParties) still has room — capacity counts guests or parties per
 * partySizeForTimeSlots, against the peak concurrent usage (filterCommunalOverCapacity →
 * findMaxOverlap on the backend).
 */
export function findSeatedConflictsOnResource<T extends SeatedConflictCandidate>(
  party: SeatedConflictCandidate,
  resourceId: string,
  parties: T[],
  options: SeatedConflictCheckOptions,
): T[] {
  const { allowMultipleParties, partySizeForTimeSlots, capacityMax, ...occupancyOptions } = options;
  const target = getPartyOccupancy(party, occupancyOptions);
  if (!target) {
    return [];
  }

  if (!occupancyOptions.servingTracking) {
    target.start = Math.max(target.start, occupancyOptions.now);
  }

  if (target.start >= target.end) {
    return [];
  }

  const conflicts: T[] = [];
  const occupancies: OccupancyWithSize[] = [];
  for (const other of parties) {
    if (other._id === party._id || !other.seated_time || other.completedAt) {
      continue;
    }

    if (!other.resources?.includes(resourceId)) {
      continue;
    }

    const occupancy = getPartyOccupancy(other, occupancyOptions);
    const stillAtTable = occupancy?.end === occupancyOptions.now && target.start <= occupancyOptions.now;
    if (occupancy && occupancy.start < target.end && (occupancy.end > target.start || stillAtTable)) {
      conflicts.push(other);
      occupancies.push({
        ...occupancy,
        size: other.size || 1,
      });
    }
  }

  if (!conflicts.length || !allowMultipleParties) {
    return conflicts;
  }

  const { maxParties, maxGuests } = findPeakConcurrentUsage(occupancies);
  if (partySizeForTimeSlots) {
    return maxGuests + (party.size || 1) > capacityMax ? conflicts : [];
  }

  return maxParties + 1 > capacityMax ? conflicts : [];
}

function findPeakConcurrentUsage(intervals: OccupancyWithSize[]): { maxParties: number; maxGuests: number } {
  const events = intervals
    .flatMap(interval => [
      { time: interval.start, parties: 1, guests: interval.size },
      { time: interval.end, parties: -1, guests: -interval.size },
    ])
    .sort((a, b) => a.time - b.time || a.parties - b.parties);

  let parties = 0;
  let guests = 0;
  let maxParties = 0;
  let maxGuests = 0;
  for (const event of events) {
    parties += event.parties;
    guests += event.guests;
    maxParties = Math.max(maxParties, parties);
    maxGuests = Math.max(maxGuests, guests);
  }

  return { maxParties, maxGuests };
}
